/**
 * Location Map Block Script
 */

class LocationMap {
  constructor(element) {
    this.element = element;
    this.mapElement = this.element.querySelector( '#location-map' );
    this.cardContainer = this.element.querySelector( '#location-card-container' );
    this.searchInput = this.element.querySelector( '.location-map__search__input' );

    if (!this.mapElement) {
      return;
    }

    // Parse data from data attributes.
    this.locations = JSON.parse( this.mapElement.dataset.locations || '[]' );
    this.center = JSON.parse( this.mapElement.dataset.center || '{"lat": 52.132633, "lng": 5.291266}' );
    this.themeUri = this.mapElement.dataset.themeUri || '';

    this.map = null;
    this.markers = [];
    this.clusterer = null;
    this.activeCard = null;
    this.activeMarker = null;
    this.infoWindow = null;
    this.markerIcons = null;

    // Wait for Google Maps API to load.
    this.initMap();
    this.initSearch();
  }

  initMap() {
    // Check if Google Maps API is loaded.
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      // Wait for API to load.
      setTimeout( () => this.initMap(), 100 );
      return;
    }

    // Marker icon URLs — served as real files to avoid Google Maps data URI caching issues.
    this.markerIconUrls = {
      default: this.themeUri + '/dist/map-marker-default.svg',
      active: this.themeUri + '/dist/map-marker-active.svg',
    };

    // Initialize map.
    this.map = new google.maps.Map( this.mapElement, {
      zoom: 10,
      center: this.center,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{visibility: 'off'}],
        },
      ],
    } );

    // Create markers for each location.
    this.createMarkers();

    // Close card when clicking on map.
    google.maps.event.addListener( this.map, 'click', () => {
      this.closeActiveCard();
      this.resetActiveMarker();
    } );
  }

  getMarkerIcon(type) {
    return {
      url: this.markerIconUrls[type],
      scaledSize: new google.maps.Size( 41, 51 ),
      anchor: new google.maps.Point( 20.5, 50 ),
    };
  }

  createMarkers() {
    this.locations.forEach( (location) => {
      const marker = new google.maps.Marker( {
        position: {lat: location.lat, lng: location.lng},
        map: this.map,
        title: location.title,
        icon: this.getMarkerIcon( 'default' ),
      } );

      marker.addListener( 'click', () => {
        this.showLocationCard( location );
        this.setActiveMarker( marker );
        this.panToMarker( location );
      } );

      this.markers.push( {marker, location} );
    } );

    // Initialize marker clustering.
    if (typeof markerClusterer !== 'undefined' && this.markers.length > 0) {
      const rawMarkers = this.markers.map( ({marker}) => marker );

      this.clusterer = new markerClusterer.MarkerClusterer( {
        map: this.map,
        markers: rawMarkers,
        renderer: {
          render: ({count, position}) => {
            return new google.maps.Marker( {
              position,
              label: {
                text: String( count ),
                color: '#fff',
                fontFamily: 'var(--font-family-headings, sans-serif)',
                fontWeight: '600',
                fontSize: '14px',
              },
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#0F4127',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
                scale: 22,
              },
              zIndex: Number( google.maps.Marker.MAX_ZINDEX ) + count,
            } );
          },
        },
      } );
    }

    // Fit bounds to show all markers if there are any.
    if (this.markers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach( ({marker}) => {
        bounds.extend( marker.getPosition() );
      } );
      this.map.fitBounds( bounds );
    }
  }

  showLocationCard(location) {
    this.closeActiveCard();

    // Create card element.
    const card = document.createElement( 'div' );
    card.className = 'location-card is-active';
    card.dataset.locationId = location.id;

    // Build terms HTML.
    let termsHTML = '';
    if (location.terms && location.terms.length > 0) {
      termsHTML = `
				<div class="location-card__divider">
					<svg viewBox="0 0 340 10" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M0 5C85 1 85 9 170 5C255 1 255 9 340 5" stroke="currentColor" stroke-width="1"/>
					</svg>
				</div>
				<div class="location-card__tags">
					${location.terms.map( (term) => {
        return `<span class="location-card__tag ${term.slug}">${term.name}</span>`;
      } ).join( '' )}
				</div>
			`;
    }

    card.innerHTML = `
			<div class="location-card__image-wrapper">
				${location.image ? `<img src="${location.image}" alt="${location.title}">` : ''}
			</div>
			<div class="location-card__content">
				<div class="location-card__header">
					<h3 class="location-card__title">
						<a href="${location.url}">${location.title}</a>
					</h3>
					<div class="location-card__address">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M12 22C12 22 20 16 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 16 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						<p>
						    <a class="location-card__address__link" target="_blank" href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent( location.address )}">
						        ${location.address}
						        <svg class="icon--external-link" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						            <path d="M14 3H21V10H19.5V5.56066L10.7803 14.2803L9.71967 13.2197L18.4393 4.5H14V3Z" style="fill:var(--icon-color, currentColor);"/>
						            <path d="M5 7C4.44772 7 4 7.44772 4 8V19C4 19.5523 4.44772 20 5 20H16C16.5523 20 17 19.5523 17 19V12H18.5V19C18.5 20.3807 17.3807 21.5 16 21.5H5C3.61929 21.5 2.5 20.3807 2.5 19V8C2.5 6.61929 3.61929 5.5 5 5.5H12V7H5Z" style="fill:var(--icon-color, currentColor);"/>
						        </svg>
						    </a>
						</p>
					</div>
				</div>
				${termsHTML}
			</div>
		`;

    this.cardContainer.appendChild( card );
    this.activeCard = card;

    // Close card when clicking outside.
    setTimeout( () => {
      document.addEventListener( 'click', this.handleOutsideClick );
    }, 10 );
  }

  closeActiveCard() {
    if (this.activeCard) {
      this.activeCard.remove();
      this.activeCard = null;
      document.removeEventListener( 'click', this.handleOutsideClick );
    }
    this.resetActiveMarker();
  }

  panToMarker(location) {
    this.map.panTo( {lat: location.lat, lng: location.lng} );

    setTimeout( () => {
      const mapDiv = this.mapElement;
      const mapWidth = mapDiv.offsetWidth;
      const mapHeight = mapDiv.offsetHeight;

      // Measure the card container to determine how much space it occupies.
      const card = this.cardContainer.querySelector( '.location-card.is-active' );

      // Vertical offset: push marker up so it sits in the visible area above the card.
      const cardHeight = card ? card.offsetHeight : 0;
      const yOffset = cardHeight > 0 ? (cardHeight / 2) - 40 : 0;

      // Horizontal offset: if the card is positioned from the left (desktop), shift marker right.
      const cardRect = this.cardContainer.getBoundingClientRect();
      const mapRect = mapDiv.getBoundingClientRect();

      // Calculate how much of the map width the card covers from the left.
      const cardLeft = cardRect.left - mapRect.left;
      const cardRight = cardRect.right - mapRect.left;
      const cardWidth = cardRight - cardLeft;

      // If card covers less than the full width, offset horizontally towards the card center.
      let xOffset = 0;
      if (cardWidth < mapWidth * 0.9) {
        // Card is on one side — shift marker towards the card's horizontal center.
        const cardCenterX = cardLeft + (cardWidth / 2);
        const mapCenterX = mapWidth / 2;
        xOffset = (cardCenterX - mapCenterX) / 2;
      }

      this.map.panBy( xOffset, yOffset );
    }, 100 );
  }

  setActiveMarker(marker) {
    // Reset previous active marker to default.
    if (this.activeMarker) {
      this.activeMarker.setIcon( this.getMarkerIcon( 'default' ) );
    }

    // Set new active marker.
    this.activeMarker = marker;
    marker.setIcon( this.getMarkerIcon( 'active' ) );
  }

  resetActiveMarker() {
    if (this.activeMarker) {
      this.activeMarker.setIcon( this.getMarkerIcon( 'default' ) );
      this.activeMarker = null;
    }
  }

  handleOutsideClick = (event) => {
    if (this.activeCard && !this.activeCard.contains( event.target )) {
      const isMarker = event.target.closest( '.gm-style' );
      if (!isMarker) {
        this.closeActiveCard();
      }
    }
  };

  initSearch() {
    if (!this.searchInput) {
      return;
    }

    // Create Places Autocomplete.
    if (typeof google === 'undefined' || typeof google.maps === 'undefined' || typeof google.maps.places === 'undefined') {
      // Wait for API to load.
      setTimeout( () => this.initSearch(), 100 );
      return;
    }

    const autocomplete = new google.maps.places.Autocomplete( this.searchInput, {
      componentRestrictions: {country: 'nl'},
      fields: ['geometry', 'name'],
    } );

    autocomplete.addListener( 'place_changed', () => {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        return;
      }

      // Pan to selected location.
      this.map.setCenter( place.geometry.location );
      this.map.setZoom( 13 );

      // Find nearest location.
      let nearest = null;
      let minDistance = Infinity;

      this.markers.forEach( ({marker, location}) => {
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          place.geometry.location,
          marker.getPosition()
        );

        if (distance < minDistance) {
          minDistance = distance;
          nearest = {marker, location};
        }
      } );

      // Show nearest location card if within 10km.
      if (nearest && minDistance < 10000) {
        this.showLocationCard( nearest.location );
        this.setActiveMarker( nearest.marker );
      }
    } );
  }
}

// Initialize all location maps on page.
document.addEventListener( 'DOMContentLoaded', () => {
  const mapBlocks = document.querySelectorAll( '.location-map' );
  mapBlocks.forEach( (block) => {
    new LocationMap( block );
  } );
} );
